import React, { useEffect, useMemo, useState } from "react";
import {
  MessageSquareText, ShoppingBag, Share2, Briefcase, GraduationCap,
  ClipboardList, Lightbulb, PenLine, Sparkles, Copy, Check, ArrowLeft, Loader2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fetchCategories, fetchTemplates, generatePrompt, startSession } from "@/lib/api";
import { getDeviceId, newRequestId } from "@/lib/device";
import { UpgradeModal } from "@/components/UpgradeModal";
import { useConfig, goToCheckout } from "@/lib/useConfig";

const ICONS = {
  MessageSquareText, ShoppingBag, Share2, Briefcase,
  GraduationCap, ClipboardList, Lightbulb, PenLine,
};

const WARNING_DAYS = 30;

export function PromptTool({ initialCategoryId = null, enforceCredits = false, onUserState = null }) {
  const [categories, setCategories] = useState([]);
  const [step, setStep] = useState("category"); // category | template | form | result
  const [activeCategory, setActiveCategory] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [preselectDone, setPreselectDone] = useState(false);
  const [userState, setUserState] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const applyUserState = (s) => {
    setUserState(s);
    if (onUserState) onUserState(s);
  };

  useEffect(() => {
    fetchCategories().then((d) => setCategories(d.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!enforceCredits) return;
    const deviceId = getDeviceId();
    startSession(deviceId).then((s) => applyUserState(s)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enforceCredits]);

  useEffect(() => {
    if (preselectDone || !initialCategoryId || !categories.length) return;
    const cat = categories.find((c) => c.id === initialCategoryId);
    if (cat) {
      setPreselectDone(true);
      selectCategory(cat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, initialCategoryId, preselectDone]);

  const selectCategory = async (cat) => {
    setActiveCategory(cat);
    setStep("template");
    setLoadingTemplates(true);
    try {
      const d = await fetchTemplates(cat.id);
      setTemplates(d.templates || []);
    } catch {
      toast.error("Não foi possível carregar os modelos. Tente novamente.");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const selectTemplate = (tpl) => {
    setActiveTemplate(tpl);
    setAnswers({});
    setResult(null);
    setStep("form");
  };

  const updateAnswer = (name, value) => setAnswers((p) => ({ ...p, [name]: value }));

  const handleGenerate = async () => {
    const hasValue = Object.values(answers).some((v) => (v || "").trim());
    if (!hasValue) {
      toast.error("Responda ao menos uma pergunta para gerar o resultado.");
      return;
    }

    // Bloqueio de crédito (apenas quando a ferramenta exige créditos)
    const isPaid = userState?.is_paid;
    if (enforceCredits && !isPaid && userState && userState.credits_remaining <= 0) {
      setShowUpgrade(true);
      return;
    }

    setGenerating(true);
    setResult(null);
    try {
      const payload = {
        category_id: activeCategory.id,
        template_id: activeTemplate.id,
        answers,
        enforce_credit: enforceCredits,
      };
      if (enforceCredits) {
        payload.device_id = getDeviceId();
        payload.request_id = newRequestId();
      }
      const data = await generatePrompt(payload);
      setResult(data);
      if (data.user_state) applyUserState(data.user_state);
      setStep("result");
    } catch (e) {
      if (e?.response?.status === 402) {
        // Créditos esgotados: atualiza estado e abre a oferta
        try {
          const s = await startSession(getDeviceId());
          applyUserState(s);
        } catch { /* noop */ }
        setShowUpgrade(true);
      } else {
        const msg = e?.response?.data?.detail || "Algo deu errado ao gerar. Tente novamente em instantes.";
        toast.error(msg);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.prompt) return;
    try {
      await navigator.clipboard.writeText(result.prompt);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = result.prompt;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Copiado!");
    setTimeout(() => setCopied(false), 2000);
  };

  const resetAll = () => {
    setStep("category");
    setActiveCategory(null);
    setActiveTemplate(null);
    setAnswers({});
    setResult(null);
  };

  return (
    <div className="glass rounded-3xl p-5 sm:p-7" data-testid="prompt-tool">
      {enforceCredits && <CreditBanner userState={userState} />}
      <StepHeader step={step} activeCategory={activeCategory} activeTemplate={activeTemplate}
        onBack={() => {
          if (step === "template") resetAll();
          else if (step === "form") setStep("template");
          else if (step === "result") setStep("form");
        }}
      />

      {step === "category" && (
        <CategoryGrid categories={categories} onSelect={selectCategory} />
      )}

      {step === "template" && (
        <TemplateList loading={loadingTemplates} templates={templates} onSelect={selectTemplate} />
      )}

      {step === "form" && activeTemplate && (
        <PromptForm
          template={activeTemplate}
          answers={answers}
          onChange={updateAnswer}
          onGenerate={handleGenerate}
          generating={generating}
        />
      )}

      {step === "result" && result && (
        <ResultView
          result={result}
          copied={copied}
          onCopy={handleCopy}
          onRegenerate={handleGenerate}
          onNew={resetAll}
          generating={generating}
        />
      )}

      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} />
    </div>
  );
}

function CreditBanner({ userState }) {
  const { config } = useConfig();

  if (!userState) {
    return (
      <div className="mb-4 rounded-2xl border border-white/8 bg-white/[0.02] px-4 py-3 text-sm text-slate-400 animate-pulse-soft" data-testid="credit-counter">
        Carregando seu acesso…
      </div>
    );
  }

  const handleRenew = () => {
    const ok = goToCheckout(config, "renew");
    if (!ok) {
      toast.info("Checkout em configuração", {
        description: "Defina a variável CHECKOUT_URL para ativar a renovação.",
      });
    }
  };

  if (userState.is_paid) {
    const days = userState.days_remaining ?? null;
    const until = userState.access_expires_at
      ? new Date(userState.access_expires_at).toLocaleDateString("pt-BR")
      : null;
    const warning = days !== null && days <= WARNING_DAYS;

    return (
      <div
        className={`mb-4 rounded-2xl border px-4 py-3 ${
          warning ? "border-amber-500/30 bg-amber-500/10" : "border-emerald-500/30 bg-emerald-500/10"
        }`}
        data-testid="credit-counter"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={`text-sm font-semibold ${warning ? "text-amber-300" : "text-emerald-400"}`}>
            {warning ? "Seu acesso está acabando" : "Acesso completo ativo"}
          </span>
          {days !== null && (
            <span className="text-xs text-slate-300" data-testid="access-days-remaining">
              {days === 0 ? "expira hoje" : `faltam ${days} ${days === 1 ? "dia" : "dias"}`}
              {until ? ` • até ${until}` : ""}
            </span>
          )}
        </div>
        {warning && (
          <button
            onClick={handleRenew}
            data-testid="renew-access-button"
            className="mt-3 w-full rounded-full bg-amber-500 py-2.5 text-sm font-bold text-slate-950 transition-all hover:bg-amber-400 active:scale-95"
          >
            Renovar acesso
          </button>
        )}
      </div>
    );
  }

  const n = userState.credits_remaining;
  let text;
  if (n <= 0) text = "Seus créditos gratuitos acabaram.";
  else if (n === 1) text = "Você tem 1 crédito gratuito.";
  else text = `Você tem ${n} créditos gratuitos.`;

  return (
    <div
      className={`mb-4 flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${
        n <= 0
          ? "border-amber-500/30 bg-amber-500/10"
          : "border-violet-500/30 bg-violet-500/10"
      }`}
      data-testid="credit-counter"
    >
      <span className={`text-sm font-semibold ${n <= 0 ? "text-amber-300" : "text-violet-200"}`}>
        {text}
      </span>
      <span className="text-xs text-slate-400">Teste gratuito</span>
    </div>
  );
}

function StepHeader({ step, activeCategory, activeTemplate, onBack }) {
  const labels = {
    category: "Escolha uma categoria",
    template: activeCategory?.name,
    form: activeTemplate?.name,
    result: "Seu resultado está pronto",
  };
  return (
    <div className="mb-5 flex items-center gap-3">
      {step !== "category" && (
        <button
          onClick={onBack}
          data-testid="tool-back-button"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
          aria-label="Voltar"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
      )}
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-violet-400" />
        <span className="font-display text-base font-semibold text-white" data-testid="tool-step-title">
          {labels[step]}
        </span>
      </div>
    </div>
  );
}

function CategoryGrid({ categories, onSelect }) {
  if (!categories.length) {
    return <div className="py-10 text-center text-sm text-slate-400 animate-pulse-soft">Carregando categorias…</div>;
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {categories.map((cat) => {
        const Icon = ICONS[cat.icon] || Sparkles;
        return (
          <button
            key={cat.id}
            onClick={() => onSelect(cat)}
            data-testid={`category-tab-${cat.id}`}
            className="group flex flex-col items-start gap-3 rounded-2xl border border-white/8 bg-white/[0.02] p-4 text-left transition-all hover:border-violet-500/50 hover:bg-violet-500/5 active:scale-95"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/20 to-cyan-400/20 text-violet-300 transition-colors group-hover:text-violet-200">
              <Icon className="h-5 w-5" />
            </span>
            <span className="text-sm font-semibold text-white">{cat.name}</span>
          </button>
        );
      })}
    </div>
  );
}

function TemplateList({ loading, templates, onSelect }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando modelos…
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {templates.map((tpl) => (
        <button
          key={tpl.id}
          onClick={() => onSelect(tpl)}
          data-testid={`template-${tpl.id}`}
          className="group rounded-2xl border border-white/8 bg-white/[0.02] p-4 text-left transition-all hover:border-violet-500/50 hover:bg-violet-500/5 active:scale-[0.98]"
        >
          <span className="block text-sm font-semibold text-white">{tpl.name}</span>
          <span className="mt-1 block text-xs leading-relaxed text-slate-400">{tpl.description}</span>
        </button>
      ))}
    </div>
  );
}

function PromptForm({ template, answers, onChange, onGenerate, generating }) {
  return (
    <div className="space-y-5">
      {template.fields.map((field) => (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={field.name} className="text-sm font-medium text-slate-200">
            {field.label}
          </Label>
          {field.type === "textarea" ? (
            <Textarea
              id={field.name}
              value={answers[field.name] || ""}
              onChange={(e) => onChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              data-testid={`prompt-form-input-${field.name}`}
              className="min-h-[90px] rounded-xl border-white/10 bg-slate-950/50 text-slate-100 placeholder:text-slate-500 focus-visible:ring-violet-500"
            />
          ) : field.type === "select" ? (
            <Select value={answers[field.name] || ""} onValueChange={(v) => onChange(field.name, v)}>
              <SelectTrigger
                data-testid={`prompt-form-input-${field.name}`}
                className="rounded-xl border-white/10 bg-slate-950/50 text-slate-100 focus:ring-violet-500"
              >
                <SelectValue placeholder="Selecione uma opção" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-slate-900 text-slate-100">
                {field.options.map((opt) => (
                  <SelectItem key={opt} value={opt} data-testid={`option-${field.name}-${opt}`}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id={field.name}
              value={answers[field.name] || ""}
              onChange={(e) => onChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              data-testid={`prompt-form-input-${field.name}`}
              className="rounded-xl border-white/10 bg-slate-950/50 text-slate-100 placeholder:text-slate-500 focus-visible:ring-violet-500"
            />
          )}
        </div>
      ))}

      <Button
        onClick={onGenerate}
        disabled={generating}
        data-testid="generate-prompt-button"
        className="h-auto w-full rounded-full bg-gradient-to-r from-violet-600 to-blue-600 py-4 text-base font-bold text-white transition-all hover:from-violet-500 hover:to-blue-500 hover:scale-[1.01] active:scale-95 disabled:opacity-70"
      >
        {generating ? (
          <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Gerando…</>
        ) : (
          <><Sparkles className="mr-2 h-5 w-5" /> GERAR</>
        )}
      </Button>
    </div>
  );
}

function ResultView({ result, copied, onCopy, onRegenerate, onNew, generating }) {
  const words = useMemo(
    () => (result?.prompt ? result.prompt.trim().split(/\s+/).length : 0),
    [result]
  );
  return (
    <div className="animate-float-up space-y-4">
      <div className="relative rounded-2xl border border-white/10 bg-slate-950/60 p-5">
        <pre
          data-testid="generated-prompt-result"
          className="whitespace-pre-wrap break-words font-mono text-sm leading-relaxed text-slate-200"
        >
          {result.prompt}
        </pre>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{words} palavras</span>
        <span>{result.mode === "ai" ? "Gerado com IA" : "Modo demonstração"}</span>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          onClick={onCopy}
          data-testid="copy-prompt-button"
          className={`h-auto flex-1 rounded-full py-4 text-base font-bold transition-all active:scale-95 ${
            copied
              ? "bg-emerald-500 text-slate-950 hover:bg-emerald-500"
              : "bg-white text-slate-950 hover:bg-slate-200"
          }`}
        >
          {copied ? (
            <><Check className="mr-2 h-5 w-5" /> Copiado!</>
          ) : (
            <><Copy className="mr-2 h-5 w-5" /> COPIAR</>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={onRegenerate}
          disabled={generating}
          data-testid="regenerate-button"
          className="h-auto rounded-full border-white/15 bg-transparent py-4 text-slate-200 hover:bg-white/5 hover:text-white"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${generating ? "animate-spin" : ""}`} /> Gerar de novo
        </Button>
      </div>

      <button
        onClick={onNew}
        data-testid="new-prompt-button"
        className="w-full text-center text-sm font-medium text-violet-300 hover:text-violet-200"
      >
        Criar outro do início
      </button>
    </div>
  );
}
